import React, { useState, useEffect } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Farm from './components/Farm';
import TaskViewer from './components/TaskViewer/TaskViewer';
import FarmPlot from './components/FarmPlot';
import Map from './components/MapViewer';
import ErrorBoundary from './components/General/ErrorBoundary';
import PlotOperation from './components/PlotOperation';
import Auth from './Auth/Auth';
import Callback from './Callback';
import i18n from './i18n';
import AppContext from './AppContext';
import { getContent } from './api';
import { getSelectedFarmId, getSelectedLang } from './common';
import { transitions, positions, Provider as AlertProvider } from 'react-alert'
import AlertTemplate from './components/Alerts/AlertTemplate'
import { useA2HS, isInStandaloneMode, isInstalledPWA } from "./A2HS.js";

const App = (props) => {
    const [geolocation, setGeolocation] = useState(null);
    const [mapInitCoordinates, setMapInitCoordinates] = useState({ coords: { longitude: 0, latitude: 0 } });
    const [selectedLanguage, setSelectedLanguage] = useState(getSelectedLang());
    const [auth, setAuth] = useState(null);
    const { isInstallable, installApp } = useA2HS();

    const isStandalone = isInStandaloneMode();
    const isPWAInstalled = isInstalledPWA();

    useEffect(() => {
        i18n.changeLanguage(getSelectedLang());
        getContent('auth', {}).then((settings) => {
            setAuth(new Auth(props.history, settings));
        }).catch(c => { console.log(c); auth.login(); });
    }, [props.history]);

    // Re-apply language once the user is authenticated, because user-scoped
    // settings (including selectedLanguage) can only be resolved after the
    // id_token is available in localStorage.
    useEffect(() => {
        if (auth && auth.isAuthenticated()) {
            const lang = getSelectedLang();
            if (lang !== i18n.language) {
                i18n.changeLanguage(lang);
            }
            setSelectedLanguage(lang);
        }
    }, [auth]);

    const alertOptions = {
        position: positions.BOTTOM_CENTER,
        timeout: 7000,
        offset: '30px',
        /* zIndex: 1060, This doesn't work for some reason */
        transition: transitions.FADE
    }

    const appInstallation = {
        isInstallable: isInstallable,
        installApp: installApp,
        standaloneMode: isStandalone,
        installedPWA: isPWAInstalled
    }

    const context = {
        geolocation: geolocation,
        setGeolocation: setGeolocation,
        setMapInitCoordinates: setMapInitCoordinates,
        mapInitCoordinates: mapInitCoordinates,
        selectedLanguage: selectedLanguage,
        auth: auth,
        appInstallation: appInstallation
    }

    const spinnerStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        /* transform: translate(-50%, -50%); */
        zIndex: 1
    }

    return (
        <>
            <ErrorBoundary {...props} auth={auth}>
                <AlertProvider className="alert-index" template={AlertTemplate} {...alertOptions}>
                    {auth === null ?

                        <div style={spinnerStyle}>
                            <Spinner as="span" animation="border" size="md" role="status" variant="secondary">
                            </Spinner> <span className="text-dark">Loading...</span>
                        </div>

                        :
                        <AppContext.Provider value={context}>
                            <Switch>
                                <Route path='/' exact render={(props) => auth.isAuthenticated() ? (<Farm {...props} />) : (auth.login(props))} />
                                <Route path='/taskViewer' render={(props) => auth.isAuthenticated() ? (getSelectedFarmId() === 0 ? <Redirect to='/' /> : <TaskViewer {...props} />) : (auth.login(props))} />
                                <Route path='/mapViewer' render={(props) => auth.isAuthenticated() ? (getSelectedFarmId() === 0 ? <Redirect to='/' /> : <Map    {...props} />) : (auth.login(props))} />
                                {/*<Route path='/settings' render={(props) => auth.isAuthenticated() ? (<FarmSettings {...props} />) : auth.login(props)} />*/}
                                <Route path='/plots' render={(props) => auth.isAuthenticated() ? (<FarmPlot {...props} />) : (auth.login(props))} />
                                <Route path='/plotOperation' render={(props) => auth.isAuthenticated() ? (<PlotOperation {...props} />) : (auth.login(props))} />
                                <Route path='/callback' render={(props) => <Callback auth={auth} {...props} />} />
                                <Route path='/logout' render={() => auth.logout()} />

                                {/* Redirect to home page if tried to open empty page */}
                                <Redirect to="/" />
                            </Switch>
                        </AppContext.Provider>
                    }
                </AlertProvider>
            </ErrorBoundary>
        </>
    );
}

export default App;